import React from 'react';

import Badge from '@/components/Badge';
import TableRowButtons from '@/components/Common/TableRowButtons';
import { DEFAULT_EMPTY_VALUE } from '@/utils/constants';
import {
  createEnumOptions,
  getEnumTranslationKey,
  parseOptionalNumericEnumFromApi,
} from '@/utils/core/data-transformation/enumHelpers';
import { CoreEnums } from '@/utils/domain/constants';
import { createTableActionsColumn, getEnumNamespace } from '@/utils/infrastructure';
import { createHighlightRender } from '@/utils/infrastructure/highlightColumns';
import Filters, { registerModuleFilters } from '@/utils/input/filters';

import { MODEL_NAME } from './constants';

const { BaseFilters } = Filters;

const SourceTypeFilter = {
  ...BaseFilters.EnumFilter,
  name: 'sourceType',
  label: 'channelCustomerAssignment.columns.sourceType',
  enumNamespace: getEnumNamespace(CoreEnums.CustomerChannelSourceType, 'Channel.Actors'),
  enumSource: CoreEnums.CustomerChannelSourceType,
  options: createEnumOptions(CoreEnums.CustomerChannelSourceType, 'customerChannelSourceType', {
    casing: 'camel',
  }),
};

const BillingModeFilter = {
  ...BaseFilters.EnumFilter,
  name: 'billingMode',
  label: 'global.text.billingMode',
  enumNamespace: getEnumNamespace(CoreEnums.ChannelBillingMode, 'Channel.Financial'),
  enumSource: CoreEnums.ChannelBillingMode,
  options: createEnumOptions(CoreEnums.ChannelBillingMode, 'channelBillingMode', {
    casing: 'camel',
  }),
};

const CustomerCompanyFilter = {
  ...BaseFilters.RemoteFilter,
  name: 'customerCompany',
  label: 'channelCustomerAssignment.columns.customerCompany',
  labelInValue: true,
  displayField: 'commercialName',
  apiRoute: 'company/paged',
  filterSearch: true,
  params: {
    select: 'id,commercialName',
    filter: 'isActive eq true',
    orderBy: 'commercialName asc',
  },
  oDataField: 'customerCompanyId',
};

const CreatedDateFilter = {
  ...Filters.DateFilter,
  name: 'created',
  label: 'global.created',
  defaultValue: CoreEnums.FilterDateRange.Disabled,
};

const UpdatedDateFilter = {
  ...Filters.DateFilter,
  name: 'updated',
  label: 'global.updated',
  defaultValue: CoreEnums.FilterDateRange.Disabled,
};

export const filtersConfig = [
  SourceTypeFilter,
  BillingModeFilter,
  CustomerCompanyFilter,
  Filters.IsActiveFilter,
  CreatedDateFilter,
  UpdatedDateFilter,
];

registerModuleFilters(MODEL_NAME, filtersConfig);

/**
 * Domain detail mapping: list-matching badges for isActive.
 * Enum selects use `_form.js` locale options.
 */
export function mapChannelCustomerAssignmentDetailFields(rows, { item }) {
  if (!item || !rows?.length) return rows;

  return rows.map((row) => {
    if (row.name === 'isActive') {
      const isActive = item.isActive === true;
      return {
        ...row,
        type: 'badge',
        badgeType: 'group-item',
        badgeStatus: isActive ? 'success' : 'default',
        value: isActive ? 'global.text.enabled' : 'global.text.disabled',
        locale: true,
      };
    }

    return row;
  });
}

const renderEnumBadge = (formatMessage, enumObject, value, translationPrefix) => {
  const resolved = parseOptionalNumericEnumFromApi(enumObject, value);
  if (resolved === undefined) {
    return <Badge status="default" type="process" text={DEFAULT_EMPTY_VALUE} />;
  }
  return (
    <Badge
      status="default"
      type="process"
      text={
        formatMessage({
          id: getEnumTranslationKey(enumObject, resolved, translationPrefix),
        }) || DEFAULT_EMPTY_VALUE
      }
    />
  );
};

export function buildChannelCustomerAssignmentColumns({
  formatMessage,
  openDrawerCreateOrEdit,
  searchCriteria,
}) {
  const highlight = createHighlightRender(searchCriteria);

  return [
    {
      key: 'isActive',
      title: formatMessage({ id: 'global.text.enabled' }),
      dataIndex: 'isActive',
      align: 'center',
      sorter: true,
      width: 120,
      render: (value) => (
        <Badge
          status={value ? 'success' : 'default'}
          type="group-item"
          text={formatMessage({
            id: value ? 'global.text.enabled' : 'global.text.disabled',
          })}
        />
      ),
    },
    {
      key: 'customerCompany/commercialName',
      title: formatMessage({ id: 'channelCustomerAssignment.columns.customerCompany' }),
      dataIndex: ['customerCompany', 'commercialName'],
      sorter: true,
      render: highlight,
    },
    {
      key: 'sourceType',
      title: formatMessage({ id: 'channelCustomerAssignment.columns.sourceType' }),
      dataIndex: 'sourceType',
      align: 'center',
      sorter: true,
      render: (value) =>
        renderEnumBadge(
          formatMessage,
          CoreEnums.CustomerChannelSourceType,
          value,
          'customerChannelSourceType',
        ),
    },
    {
      key: 'billingMode',
      title: formatMessage({ id: 'global.text.billingMode' }),
      dataIndex: 'billingMode',
      align: 'center',
      sorter: true,
      render: (value) =>
        renderEnumBadge(formatMessage, CoreEnums.ChannelBillingMode, value, 'channelBillingMode'),
    },
    createTableActionsColumn({
      inlineActionCount: 1,
      render: (record) => (
        <TableRowButtons
          onDetail={() => openDrawerCreateOrEdit(record.id, true)}
          formatMessage={formatMessage}
          actionOrder={{ primary: ['detail'], secondary: [] }}
        />
      ),
    }),
  ];
}
